<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { scansApi } from '@/api/scansApi'
import { toApiError } from '@/api/httpClient'
import type { ImageFilter, ScanDto } from '@/models/library'

const props = defineProps<{ scan: ScanDto }>()
const emit = defineEmits<{ (e: 'close'): void; (e: 'delete'): void; (e: 'changed'): void }>()

const router = useRouter()
const current = ref<ScanDto>({ ...props.scan })
const busy = ref(false)
const error = ref<string | null>(null)
let changed = false

const isPdf = computed(() => current.value.kind === 'Pdf')
const contentUrl = computed(() =>
  scansApi.contentUrl(current.value.id, { version: current.value.updatedUtc })
)
const downloadUrl = computed(() => scansApi.contentUrl(current.value.id, { download: true }))

const filters: { key: ImageFilter; label: string }[] = [
  { key: 'Original', label: 'Original' },
  { key: 'EnhancedColor', label: 'Color' },
  { key: 'Grayscale', label: 'Gray' },
  { key: 'BlackAndWhite', label: 'B&W' }
]

async function applyFilter(filter: ImageFilter): Promise<void> {
  busy.value = true
  error.value = null
  try {
    current.value = await scansApi.process(current.value.id, { filter })
    changed = true
  } catch (err) {
    error.value = toApiError(err).title
  } finally {
    busy.value = false
  }
}

async function rotate(): Promise<void> {
  busy.value = true
  error.value = null
  try {
    current.value = await scansApi.rotate(current.value.id, 90)
    changed = true
  } catch (err) {
    error.value = toApiError(err).title
  } finally {
    busy.value = false
  }
}

const ocrText = ref<string | null>(null)
const ocrBusy = ref(false)

async function runOcr(): Promise<void> {
  ocrBusy.value = true
  error.value = null
  try {
    current.value = await scansApi.ocr(current.value.id)
    changed = true
    ocrText.value = await scansApi.ocrText(current.value.id)
  } catch (err) {
    error.value = toApiError(err).title
  } finally {
    ocrBusy.value = false
  }
}

function openCrop(): void {
  emitCloseWithRefresh(false)
  router.push(`/library/${current.value.folderId}/scans/${current.value.id}/crop`)
}

function emitCloseWithRefresh(close = true): void {
  if (changed) emit('changed')
  if (close) emit('close')
}
</script>

<template>
  <div class="overlay" @click.self="emitCloseWithRefresh()">
    <div class="modal">
      <header class="modal-head">
        <span class="title" :title="current.name">{{ current.name }}</span>
        <button class="icon-btn" @click="emitCloseWithRefresh()" aria-label="Close">✕</button>
      </header>

      <div class="viewer">
        <iframe v-if="isPdf" :src="contentUrl" :title="current.name" />
        <img v-else :src="contentUrl" :alt="current.name" />
      </div>

      <!-- Enhancement filters (images only) -->
      <div v-if="!isPdf" class="filters">
        <button
          v-for="f in filters"
          :key="f.key"
          class="filter"
          :class="{ active: current.filterType === f.key }"
          :disabled="busy"
          @click="applyFilter(f.key)"
        >
          {{ f.label }}
        </button>
      </div>

      <pre v-if="ocrText" class="ocr-panel">{{ ocrText }}</pre>

      <p v-if="error" class="err">{{ error }}</p>

      <footer class="modal-foot">
        <button v-if="!isPdf" class="btn" :disabled="busy" @click="rotate">↻</button>
        <button v-if="!isPdf" class="btn" @click="openCrop">✂ Crop</button>
        <button v-if="!isPdf" class="btn" :disabled="ocrBusy" @click="runOcr">
          {{ ocrBusy ? 'OCR…' : '🔤 OCR' }}
        </button>
        <a class="btn btn-primary" :href="downloadUrl">Download</a>
        <button class="btn danger" @click="emit('delete')">Delete</button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 50;
}

.modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  width: min(900px, 100%);
  max-height: 90dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-head,
.modal-foot {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
}

.modal-head {
  justify-content: space-between;
  border-bottom: 1px solid var(--border);
}

.modal-foot {
  gap: 0.5rem;
  border-top: 1px solid var(--border);
}

.title {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.viewer {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg);
  overflow: auto;
}

.viewer img {
  max-width: 100%;
  max-height: 66dvh;
  object-fit: contain;
}

.viewer iframe {
  width: 100%;
  height: 72dvh;
  border: 0;
  background: #fff;
}

.filters {
  display: flex;
  gap: 0.4rem;
  padding: 0.6rem 1rem;
  justify-content: center;
  border-top: 1px solid var(--border);
}

.filter {
  flex: 1;
  max-width: 6rem;
  background: var(--surface-2);
  border: 1px solid var(--border);
  color: var(--text);
  border-radius: 8px;
  padding: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.filter.active {
  background: var(--primary);
  border-color: var(--primary-strong);
  color: var(--on-primary);
}

.filter:disabled {
  opacity: 0.6;
}

.icon-btn {
  background: transparent;
  border: 0;
  color: var(--text);
  font-size: 1.1rem;
  cursor: pointer;
}

.btn.danger {
  border-color: var(--danger);
  color: var(--danger);
}

.err {
  color: var(--danger);
  text-align: center;
  margin: 0.25rem 0;
}

.ocr-panel {
  max-height: 22dvh;
  overflow: auto;
  margin: 0.5rem 1rem;
  padding: 0.6rem 0.75rem;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 0.8rem;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--text);
}
</style>
